/**
 * Sparkline — Lightweight inline SVG sparkline chart.
 *
 * Pure UI component — no data fetching, just renders a polyline
 * from an array of numbers. Green for uptrend, red for downtrend, gray for flat.
 * Hidden on mobile via className prop.
 *
 * Story 8.4 — Task 3
 */

import { useMemo } from "react";

type SparklineProps = {
	data: number[];
	width?: number;
	height?: number;
	color?: string;
	className?: string;
};

function getTrendColor(data: number[]): string {
	const first = data[0];
	const last = data[data.length - 1];
	if (first === undefined || last === undefined) return "#9ca3af";
	if (last > first) return "#22c55e";
	if (last < first) return "#ef4444";
	return "#9ca3af";
}

export function Sparkline({ data, width = 60, height = 20, color, className }: SparklineProps) {
	const points = useMemo(() => {
		if (!data || data.length < 2) return null;

		let min = data[0] ?? 0;
		let max = data[0] ?? 0;
		for (const v of data) {
			if (v < min) min = v;
			if (v > max) max = v;
		}
		const range = max - min || 1;

		return data
			.map((v, i) => {
				const x = (i / (data.length - 1)) * width;
				const y = height - ((v - min) / range) * height;
				return `${String(x)},${String(y)}`;
			})
			.join(" ");
	}, [data, width, height]);

	if (!points) return null;

	const trendColor = color ?? getTrendColor(data);

	return (
		<svg
			width={width}
			height={height}
			viewBox={`0 0 ${String(width)} ${String(height)}`}
			className={className}
			data-testid="sparkline"
			role="img"
			aria-label="Price sparkline"
		>
			<title>Price sparkline</title>
			<polyline
				points={points}
				fill="none"
				stroke={trendColor}
				strokeWidth="1.5"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}
